/** Parent personal-account form section. */
import type { ChangeEvent, ReactNode } from 'react';
import { IdCard, KeyRound, Mail, User } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { AddressAutocompleteInput } from '@/components/forms/AddressAutocompleteInput';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import type { StoredContact } from '@/components/forms/SmartContactInput';
import { ProfessionalSituationFields } from '@/components/common/ProfessionalSituationFields';
import {
  BOOLEAN_SELECT_OPTIONS,
  GENDER_OPTIONS_WITHOUT_OTHER,
  PARENT_ACCOUNT_STATUS_CONFIG,
} from '@/components/parents/parent-configs';
import type { ParentFormValues } from '@/types/parent.types';
import {
  applyParentAddressSelection,
  sanitizeAccessCode,
  type ParentContactType,
} from '@/components/parents/parent-form-utils';
import {
  booleanRadioValue,
  canSwapPrimaryContact,
  contactExistsErrorMessage,
  oppositeContactType,
  primaryContactFieldError,
} from '@/utils/user-contact-utils';
import { Button } from '@/components/forms/Button';
import type { AddressSelectionResult } from '@/types/geography.types';

/** Props for {@link ParentPersonalAccountSection}. */
type Props = Readonly<{
  formData: ParentFormValues;
  onChange: (key: string, value: unknown) => void;
  onBlur?: (field: string) => void;
  validation?: any;
  mode: 'create' | 'edit';
  defaultCountry: string;
  contactType: ParentContactType;
  onContactChange: (value: string, type: ParentContactType, phoneCountryCode?: string) => void;
  onSwapContacts?: () => void;
  /** Edit mode only */
  userId?: string;
  emailVerified?: boolean;
  /** Primary contact exists check result (edit: checks primary; create: checks primary too) */
  contactExists?: boolean | null;
  /** Secondary contact exists check result (create mode only) */
  secondaryContactExists?: boolean | null;
  /** Edit: additional primary exists from dedicated hook */
  primaryContactExists?: boolean | null;
  /** Edit: whether primary and secondary are identical */
  contactsAreIdentical?: boolean;
  /** Edit: server email for dirty check */
  serverEmail?: string | null;
  /** Edit: server phone for dirty check */
  serverPhone?: string | null;
  /** Edit: server primary kind for dirty check */
  serverPrimaryKind?: 'email' | 'phone' | null;
}>;

/** Parent personal-account form section. */
export function ParentPersonalAccountSection({
  formData,
  onChange,
  onBlur,
  validation,
  mode,
  defaultCountry,
  contactType,
  onContactChange,
  onSwapContacts,
  userId,
  emailVerified,
  contactExists,
  secondaryContactExists,
  primaryContactExists,
  contactsAreIdentical,
  serverEmail,
  serverPhone,
  serverPrimaryKind,
}: Props) {
  const isEdit = mode === 'edit';
  const effectivePrimaryExists = isEdit ? primaryContactExists : contactExists;
  const contactExistsError = primaryContactFieldError(
    effectivePrimaryExists,
    contactType,
    !!contactsAreIdentical,
  );
  const secondaryContactType = oppositeContactType(contactType);
  const secondaryContactExistsError = contactExistsErrorMessage(
    isEdit ? contactExists : secondaryContactExists,
    secondaryContactType,
  );
  const contactFormatError = validation?.getFieldError?.('contact') as string | undefined;
  const secondaryFormatError = validation?.getFieldError?.('secondaryContact') as
    | string
    | undefined;
  const contactFieldError = contactExistsError ?? contactFormatError;
  const secondaryFieldError = secondaryContactExistsError ?? secondaryFormatError;

  /** Swap beside the primary field only while the primary is a real email or phone. */
  const swapButton =
    isEdit && onSwapContacts && canSwapPrimaryContact(contactType) ? (
      <Button type="button" preset="contact-swap" size="md" onClick={onSwapContacts} />
    ) : undefined;

  /** storedContact with live primaryKind so the field mode syncs after a swap. */
  const liveStoredContact: StoredContact | undefined = isEdit
    ? {
        primaryKind: contactType as 'email' | 'phone',
        serverPrimaryKind,
        email: serverEmail,
        phone: serverPhone,
      }
    : undefined;

  /** Apply BAN autocomplete into address / zip / city / country. */
  function handleAddressSelection(result: AddressSelectionResult): void {
    applyParentAddressSelection(onChange, result);
  }

  /** Persist share-phone consent. */
  function handleAcceptSharePhone(value: string): void {
    onChange('acceptSharePhone', value === 'true');
  }

  /** Persist studies-participation flag. */
  function handleParticipatedToStudies(value: string): void {
    onChange('isParticipatedToStudies', value === 'true');
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <IdCard className="w-4 h-4" />
        Compte et informations personnelles
      </h4>

      <div className="form-section">
        <h4 className="form-section-title">
          <KeyRound className="w-4 h-4" />
          Compte
        </h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="Prénom"
            value={formData.firstName ?? ''}
            onChange={(e) => onChange('firstName', e.target.value)}
            onBlur={() => onBlur?.('firstName')}
            required
            state={validation?.getFieldState?.('firstName')}
            error={validation?.getFieldError?.('firstName')}
            inputSize="sm"
            placeholder="Ex: Marie"
          />
          <Input
            label="Nom"
            value={formData.lastName ?? ''}
            onChange={(e) => onChange('lastName', e.target.value)}
            onBlur={() => onBlur?.('lastName')}
            required
            state={validation?.getFieldState?.('lastName')}
            error={validation?.getFieldError?.('lastName')}
            inputSize="sm"
            placeholder="Ex: Martin"
          />
          <Select
            label="Genre"
            value={formData.gender ?? ''}
            onChange={(val) => onChange('gender', val)}
            onBlur={() => onBlur?.('gender')}
            options={[...GENDER_OPTIONS_WITHOUT_OTHER]}
            size="sm"
            placeholder="Sélectionner"
            required
            state={validation?.getFieldState?.('gender')}
            error={validation?.getFieldError?.('gender')}
          />
        </div>
        {formData.status === 'archived' ? (
          <Input
            label="Statut"
            value={PARENT_ACCOUNT_STATUS_CONFIG.archived.label}
            disabled
            inputSize="sm"
          />
        ) : null}
        <div className="grid grid-cols-2 items-start gap-2 mt-4">
          <SmartContactInput
            label="Contact"
            value={formData.contact ?? formData.email ?? formData.phone ?? ''}
            onContactChange={onContactChange}
            onBlur={() => onBlur?.('contact')}
            required
            state={contactFieldError ? 'error' : undefined}
            error={contactFieldError}
            helperText={isEdit ? undefined : 'Email ou téléphone du parent'}
            inputSize="sm"
            storedContact={liveStoredContact}
            labelTooltip={undefined}
            showContactActions={isEdit}
            emailVerified={emailVerified}
            userId={userId}
            swapButton={swapButton}
          />
          <ParentSecondaryContact
            contactType={contactType}
            formData={formData}
            defaultCountry={defaultCountry}
            fieldError={secondaryFieldError}
            onChange={onChange}
            onBlur={onBlur}
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <label htmlFor="parent-access-code" className="form-label form-label-required order-1">
            Code de connexion
          </label>
          <span className="form-label order-4 sm:order-2">
            Accepte de partager ses coordonnées avec les tuteurs de ses enfants
          </span>
          <div className="order-2 sm:order-3">
            <Input
              id="parent-access-code"
              value={formData.accessCode ?? ''}
              onChange={(e) => onChange('accessCode', sanitizeAccessCode(e.target.value))}
              required
              inputSize="sm"
              placeholder="000000"
              maxLength={6}
              className="font-mono tracking-wider"
              state={validation?.getFieldState?.('accessCode')}
              error={validation?.getFieldError?.('accessCode')}
            />
          </div>
          <div className="order-5 flex h-9 items-center sm:order-4">
            <RadioGroup
              className="space-y-0 [&_label]:text-sm"
              name="acceptSharePhone"
              layout="horizontal"
              options={BOOLEAN_SELECT_OPTIONS}
              value={booleanRadioValue(formData.acceptSharePhone)}
              onValueChange={handleAcceptSharePhone}
            />
          </div>
          <p className="form-helper-text order-3 sm:order-5">6 chiffres exactement</p>
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <User className="w-4 h-4" />
          Informations personnelles
        </h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 min-w-0">
          <AddressAutocompleteInput
            label="Adresse"
            value={formData.address ?? ''}
            onChange={(value) => onChange('address', value)}
            size="sm"
            required
            state={validation?.getFieldState?.('address')}
            error={validation?.getFieldError?.('address')}
            onSelection={handleAddressSelection}
          />
          <Input
            label="Code postal"
            value={formData.zipCode ?? ''}
            onChange={(e) => onChange('zipCode', e.target.value)}
            onBlur={() => onBlur?.('zipCode')}
            required
            inputSize="sm"
            placeholder="Ex: 75001"
            state={validation?.getFieldState?.('zipCode')}
            error={validation?.getFieldError?.('zipCode')}
          />
          <Input
            label="Ville"
            value={formData.city ?? ''}
            onChange={(e) => onChange('city', e.target.value)}
            onBlur={() => onBlur?.('city')}
            required
            inputSize="sm"
            placeholder="Ex: Paris"
            state={validation?.getFieldState?.('city')}
            error={validation?.getFieldError?.('city')}
          />
          <Input
            label="Pays"
            value={formData.country ?? ''}
            onChange={(e) => onChange('country', e.target.value)}
            onBlur={() => onBlur?.('country')}
            required
            inputSize="sm"
            placeholder="Ex: France"
            state={validation?.getFieldState?.('country')}
            error={validation?.getFieldError?.('country')}
          />
        </div>
        <div className="mt-4 grid min-w-0 grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <span className="form-label form-label-required order-1">Date de naissance</span>
          <span className="form-label form-label-required order-3 sm:order-2">Département</span>
          <div className="order-2 min-w-0 sm:order-3">
            <DateSplitInput
              value={formData.dateOfBirth ?? ''}
              onChange={(value) => onChange('dateOfBirth', value)}
              onBlur={() => onBlur?.('dateOfBirth')}
              required
              maxDate={`${new Date().getFullYear() - 18}-12-31`}
              size="sm"
              state={validation?.getFieldState?.('dateOfBirth')}
              error={validation?.getFieldError?.('dateOfBirth')}
            />
          </div>
          <div className="order-4 min-w-0 self-start">
            <DepartmentSelect
              label=""
              className="min-w-0"
              value={formData.departmentId ?? ''}
              onChange={(val) => onChange('departmentId', val)}
              placeholder="Sélectionner"
              inputSize="sm"
              required
            />
          </div>
        </div>

        <ProfessionalSituationFields formData={formData} onChange={onChange} persona="parent" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-start mt-4">
          <RadioGroup
            className="sm:col-span-2"
            name="isParticipatedToStudies"
            label="Souhaite être contacté(e) pour partager son expérience ?"
            layout="horizontal"
            options={BOOLEAN_SELECT_OPTIONS}
            value={booleanRadioValue(formData.isParticipatedToStudies)}
            onValueChange={handleParticipatedToStudies}
          />
        </div>
      </div>
    </div>
  );
}

/** Props for {@link ParentSecondaryContact}. */
type ParentSecondaryContactProps = Readonly<{
  contactType: ParentContactType;
  formData: ParentFormValues;
  defaultCountry: string;
  fieldError: string | undefined;
  onChange: (key: string, value: unknown) => void;
  onBlur?: (field: string) => void;
}>;

/** Secondary email or phone input, opposite of the primary contact. */
function ParentSecondaryContact({
  contactType,
  formData,
  defaultCountry,
  fieldError,
  onChange,
  onBlur,
}: ParentSecondaryContactProps): ReactNode {
  if (contactType === 'unknown') return null;

  /** Persist secondary contact value. */
  function handleSecondaryChange(event: ChangeEvent<HTMLInputElement>): void {
    onChange('secondaryContact', event.target.value);
  }

  /** Mark secondary contact touched. */
  function handleSecondaryBlur(): void {
    onBlur?.('secondaryContact');
  }

  /** Persist secondary-phone country ISO. */
  function handleSecondaryCountryChange(country: { code: string }): void {
    onChange('secondaryContactCountryCode', country.code);
  }

  if (contactType === 'email') {
    return (
      <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
        <PhoneInput
          label="Téléphone (contact secondaire, optionnel)"
          value={formData.secondaryContact ?? ''}
          onChange={handleSecondaryChange}
          onBlur={handleSecondaryBlur}
          countryCode={formData.secondaryContactCountryCode ?? defaultCountry}
          onCountryChange={handleSecondaryCountryChange}
          inputSize="sm"
          state={fieldError ? 'error' : 'default'}
          error={fieldError}
        />
      </div>
    );
  }
  return (
    <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
      <Input
        type="text"
        label="Email (contact secondaire, optionnel)"
        value={formData.secondaryContact ?? ''}
        onChange={handleSecondaryChange}
        onBlur={handleSecondaryBlur}
        icon={Mail}
        placeholder="Ex: contact@example.com"
        inputSize="sm"
        state={fieldError ? 'error' : 'default'}
        error={fieldError}
      />
    </div>
  );
}
